import { Component, computed, effect, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { SwiperComponent } from '../../../common/components';
import { BorderConfig, Breakpoint, CardOrienationOption, CarouselWidgetSlide, ColorTheme, ContentTypeOption, MediaTypeOption, NodeModel } from '../../../common/models';
import { ColorHelperService } from '../../../common/services';
import { SLIDE_BUILDER_BACKGROUND_CONTEXT } from '../../../common/constants';

@Component({
    selector: 'lib-carousel-widget',
    templateUrl: './carousel-widget.component.html',
    styleUrls: ['./carousel-widget.component.scss'],
    standalone: false
})
export class CarouselWidgetComponent {
  public swiperComponent = viewChild(SwiperComponent);
  public readonly parentBackgroundContext = inject(SLIDE_BUILDER_BACKGROUND_CONTEXT, { optional: true });
  public readonly colorHelperService = inject(ColorHelperService);

  public colorTheme = computed(() => {
    const context = this.parentBackgroundContext?.();

    if (!context?.enableBackground || !context.backgroundColor) {
      return null;
    }

    const isDark = this.colorHelperService.isColorDark(context.backgroundColor);

    return isDark ? ColorTheme.LIGHT : ColorTheme.DARK;
  });

  public widgetCssClass = computed(() => {
    return {
      'carousel-theme-light': this.colorTheme() === ColorTheme.LIGHT,
      'carousel-theme-dark': this.colorTheme() === ColorTheme.DARK,
      'multiple-slider-view': this.multiplePerView(),
      'preview-mode': this.previewMode(),
      'static-view': this.slidesFitInView()
    }
  });

  public previewContainerBreakpoint = input<Breakpoint>();
  public items = input<CarouselWidgetSlide[]>([]);
  public cardOrientation = input<CardOrienationOption>();

  public dropShadow = input<boolean>(false);
  public disableRoundness = input<boolean>(false);
  public border = input<BorderConfig>();

  public hideNavigationButtons = input<boolean>(false);
  public enableCardEmptyState = input<boolean>(false);
  public showCardEditButton = input<boolean>(false);
  public previewMode = input<boolean>(false);
  public isTranslation = input<boolean>(false);

  public readonly slidesPerViewInput = input<number>(1, { alias: 'slidesPerView' });

  public readonly slidesPerView = computed(() => {
    const value = this.slidesPerViewInput();
    return Math.max(this.MIN_PER_VIEW, Math.min(this.MAX_PER_VIEW, value));
  });

  public edit = output<number>();
  public slideViewed = output<number>();
  public navigateToContent = output<NodeModel>();

  public swiperOptions = computed(() => {
    const mobileOptions = {
      slidesPerGroup: 1,
      slidesPerView: 1,
      grid: {
        rows: this.slidesFitInView() ? this.slidesPerView() : 1
      }
    };

    const desktopOptions = {
      slidesPerGroup: this.slidesPerView(),
      slidesPerView: this.slidesPerView(),
      grid: {
        rows: 1
      }
    };

    const breakpoints = this.previewContainerBreakpoint()
      ? { 0: this.previewContainerBreakpoint() === Breakpoint.DESKTOP ? desktopOptions : mobileOptions }
      : { 0: mobileOptions, 992: desktopOptions };

    return {
      spaceBetween: 20,
      loop: false,
      breakpoints
    };
  });

  public readonly mediaTypes: typeof MediaTypeOption = MediaTypeOption;
  public readonly contentTypes: typeof ContentTypeOption = ContentTypeOption;

  public multiplePerView = computed(() => this.slidesPerView() > 1);
  public slidesFitInView = computed(() => this.items().length <= this.slidesPerView());

  private readonly MAX_PER_VIEW = 5;
  private readonly MIN_PER_VIEW = 1;

  constructor(public readonly element: ElementRef) {
    effect(() => {
      this.items();
      this.previewContainerBreakpoint();
      this.slidesPerView();
  
      this.resetSwiperConfiguration();
    });
  }

  public slideIndexChange(index: number): void {
    const end = index + this.slidesPerView() - 1;

    for (let i = index; i <= end; i++) {
      this.slideViewed.emit(i);
    }
  }

  private resetSwiperConfiguration(): void {
    this.element.nativeElement.style.setProperty('--lms-slides-per-view', this.slidesPerView());
    const swiperComponent = this.swiperComponent();

    if (swiperComponent?.swiper) {
      const bp: 0 | 992 = swiperComponent.swiper.currentBreakpoint ?? 0;
      const bpParams = this.swiperOptions().breakpoints[bp] ?? this.swiperOptions().breakpoints[0];
      Object.assign(swiperComponent.swiperContainerRef.nativeElement, bpParams);
      swiperComponent.swiper.update();
    }
  }
}
